import Component from '@bythepixel/component-loader';

const PLAY_THRESHOLD = 0.25;
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

const STATE = {
  playing: 'playing',
  paused: 'paused',
};

/**
 * Muted, looping background <video>. Plays while in view and pauses when
 * scrolled out. An optional [data-video-toggle] button lets the user pause;
 * an explicit pause is remembered so re-entering the viewport doesn't resume.
 * Reduced-motion users start paused on the poster with the toggle available.
 */
export default class AutoplayVideo extends Component {
  static selector = '[comp-autoplay-video]';

  #video = null;
  #toggle = null;
  #observer = null;
  #userPaused = false;

  constructor(...args) {
    super(...args);
    this.init();
  }

  init() {
    this.#video = this.$container.matches('video')
      ? this.$container
      : this.$container.querySelector('video');

    if (!this.#video) {
      return;
    }

    this.#toggle = this.$container.querySelector('[data-video-toggle]');
    this.#userPaused = window.matchMedia(REDUCED_MOTION).matches;

    // Set via property as well as the attribute: Chrome gates no-gesture
    // autoplay on the live muted state.
    this.#video.muted = true;

    // Drive the toggle off the element's own events so it stays truthful even
    // when the browser blocks autoplay.
    this.#video.addEventListener('play', () => this.syncToggle(STATE.playing));
    this.#video.addEventListener('pause', () => this.syncToggle(STATE.paused));
    this.syncToggle(STATE.paused);

    if (this.#toggle) {
      this.#toggle.addEventListener('click', () => this.onToggle());
    }

    this.observe();
  }

  observe() {
    if (!('IntersectionObserver' in window)) {
      this.autoplay();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= PLAY_THRESHOLD) {
            this.autoplay();
          } else {
            this.pause();
          }
        });
      },
      { threshold: [0, PLAY_THRESHOLD] },
    );

    this.#observer.observe(this.#video);
  }

  autoplay() {
    if (!this.#userPaused) {
      this.play();
    }
  }

  onToggle() {
    this.#userPaused = !this.#video.paused;

    if (this.#userPaused) {
      this.pause();
    } else {
      this.play();
    }
  }

  play() {
    const promise = this.#video.play();

    if (promise && typeof promise.catch === 'function') {
      promise.catch(() => {}); // autoplay blocked; poster stays visible
    }
  }

  pause() {
    if (!this.#video.paused) {
      this.#video.pause();
    }
  }

  syncToggle(state) {
    if (this.#toggle) {
      this.#toggle.dataset.state = state;
    }
  }

  destroy() {
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
  }
}
